
/**
 * Currency utility functions
 */

// List of supported currencies with their symbols
export const currencies = [
  { code: 'GBP', symbol: '£', locale: 'en-GB' },
  { code: 'USD', symbol: '$', locale: 'en-US' },
  { code: 'EUR', symbol: '€', locale: 'de-DE' },
  { code: 'CAD', symbol: 'C$', locale: 'en-CA' },
  { code: 'AUD', symbol: 'A$', locale: 'en-AU' },
  { code: 'NGN', symbol: '₦', locale: 'en-NG' },
  { code: 'INR', symbol: '₹', locale: 'en-IN' }
];

// Country to currency mapping
const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  'GB': 'GBP',
  'US': 'USD',
  'CA': 'CAD',
  'AU': 'AUD',
  'DE': 'EUR',
  'FR': 'EUR',
  'ES': 'EUR',
  'IT': 'EUR',
  'NL': 'EUR',
  'IE': 'EUR',
  'NG': 'NGN',
  'IN': 'INR',
  // Add more mappings as needed
};

// Store the detected or selected currency
let userCurrency: string | null = null;

/**
 * Get currency from country code
 */
export const getCurrencyFromCountry = (countryCode: string): string => {
  return COUNTRY_CURRENCY_MAP[countryCode.toUpperCase()] || 'GBP';
};

/**
 * Get the user-specific preference key for currency
 */
const getUserCurrencyKey = (userId?: string): string => {
  if (userId) {
    return `defaultCurrency_${userId}`;
  }
  return 'userCurrency'; // fallback for anonymous users
};

/**
 * Get the user's preferred currency from localStorage
 */
export const getUserCurrency = (userId?: string): string => {
  const key = getUserCurrencyKey(userId);
  const storedCurrency = localStorage.getItem(key);
  
  if (storedCurrency) {
    userCurrency = storedCurrency;
    return storedCurrency;
  }
  
  return detectUserCurrency(userId);
};

/**
 * Set the user's preferred currency in localStorage
 */
export const setUserCurrency = (currency: string, userId?: string): void => {
  const key = getUserCurrencyKey(userId);
  localStorage.setItem(key, currency);
  userCurrency = currency;
  
  // Also set the generic key for backward compatibility
  localStorage.setItem('userCurrency', currency);
};

/**
 * Initialize currency from user's country during registration
 */
export const initializeCurrencyFromCountry = (countryCode: string, userId?: string): string => {
  const currency = getCurrencyFromCountry(countryCode);
  setUserCurrency(currency, userId);
  return currency;
};

/**
 * Detect the user's currency based on their browser locale or stored country
 * Falls back to GBP if detection fails
 */
export const detectUserCurrency = (userId?: string): string => {
  // If we've already detected the currency, return it
  if (userCurrency) return userCurrency;
  
  try {
    // First try to get from user's stored country preference
    if (userId) {
      const storedCountry = localStorage.getItem(`userCountry_${userId}`);
      if (storedCountry) {
        const currency = getCurrencyFromCountry(storedCountry);
        setUserCurrency(currency, userId);
        return currency;
      }
    }
    
    // Try to get the user's locale from the browser
    const userLocale = navigator.language;
    // Detected user locale
    
    // Get currency from locale (simplified approach)
    if (userLocale.includes('en-GB') || userLocale.includes('en-UK')) {
      userCurrency = 'GBP';
    } else if (userLocale.includes('en-US')) {
      userCurrency = 'USD';
    } else if (userLocale.includes('en-CA')) {
      userCurrency = 'CAD';
    } else if (userLocale.includes('en-AU')) {
      userCurrency = 'AUD';
    } else if (userLocale.includes('de') || userLocale.includes('fr') || userLocale.includes('es') || userLocale.includes('it')) {
      userCurrency = 'EUR';
    } else if (userLocale.includes('in')) {
      userCurrency = 'INR';
    } else {
      // Default to GBP if no match
      userCurrency = 'GBP';
    }
    
    // Save to localStorage for persistence
    setUserCurrency(userCurrency, userId);
    
    return userCurrency;
  } catch (error) {
    console.error('Currency detection error');
    return 'GBP'; // Default fallback
  }
};

/**
 * Get the currency symbol for the current user currency
 */
export const getCurrencySymbol = (userId?: string): string => {
  const code = getUserCurrency(userId);
  const currency = currencies.find(c => c.code === code);
  return currency?.symbol || '£'; // Default to £ if not found
};

/**
 * Format a number as currency using the detected locale and currency
 */
export const formatCurrency = (value: number, userId?: string): string => {
  const code = getUserCurrency(userId);
  const currency = currencies.find(c => c.code === code);
  
  return new Intl.NumberFormat(currency?.locale || 'en-GB', {
    style: 'currency',
    currency: code,
    maximumFractionDigits: 0
  }).format(value);
};
